import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Building2, Save, Server, Copy, Check, RefreshCw, Wallet, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Breadcrumbs } from '../components/layout/Breadcrumbs';
import { QRSecretCard } from '../components/settings/QRSecretCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { useAuth } from '../providers/AuthProvider';
import { getUserDisplayName } from '@tco/shared-types';
import { useTenantSettings, useUpdateTenantSettings, useOrganizationInfo } from '../hooks/settings/useSettings';
import { useSync1CDirectories } from '../hooks/settings/useSync1C';
import { useSyncKpiBonuses, type KpiBonusSyncResponse } from '../hooks/settings/useKpiSync';
import { SyncResultsDialog } from '../components/settings/SyncResultsDialog';
import type { OneCSync_Response } from '../types/onec-sync.types';

export function Settings() {
  const { user } = useAuth();
  const { data: settings, isLoading: isLoadingSettings } = useTenantSettings();
  const { data: organizationInfo, isLoading: isLoadingOrganization } = useOrganizationInfo();
  const updateSettings = useUpdateTenantSettings();

  // 1C Sync hook and state
  const sync1C = useSync1CDirectories();
  const [showSyncResults, setShowSyncResults] = useState(false);
  const [syncResults, setSyncResults] = useState<OneCSync_Response | null>(null);

  // KPI bonus sync
  const kpiSync = useSyncKpiBonuses();
  const [kpiSyncResult, setKpiSyncResult] = useState<KpiBonusSyncResponse | null>(null);
  const [kpiSyncError, setKpiSyncError] = useState<string | null>(null);

  // Local state for director name input
  const [directorName, setDirectorName] = useState('');
  const [isDirectorNameDirty, setIsDirectorNameDirty] = useState(false);

  // Local state for 1C integration fields
  const [onecApiUrl, setOnecApiUrl] = useState('');
  const [onecApiUsername, setOnecApiUsername] = useState('');
  const [onecApiPassword, setOnecApiPassword] = useState('');
  const [onecTimeoutMs, setOnecTimeoutMs] = useState(60000);
  const [is1CSettingsDirty, setIs1CSettingsDirty] = useState(false);

  // Local state for tenant ID copy
  const [isTenantIdCopied, setIsTenantIdCopied] = useState(false);

  // Sync director name from settings
  useEffect(() => {
    if (settings?.director_name !== undefined) {
      setDirectorName(settings.director_name || '');
      setIsDirectorNameDirty(false);
    }
  }, [settings?.director_name]);

  // Sync 1C settings from API
  useEffect(() => {
    if (settings) {
      setOnecApiUrl(settings.onec_api_url || '');
      setOnecApiUsername(settings.onec_api_username || '');
      setOnecApiPassword(''); // Never populate password from API
      setOnecTimeoutMs(settings.onec_timeout_ms || 60000);
      setIs1CSettingsDirty(false);
    }
  }, [settings]);

  const handleDirectorNameSave = async () => {
    try {
      await updateSettings.mutateAsync({
        director_name: directorName || undefined,
      });
      setIsDirectorNameDirty(false);
      console.log('Руководитель организации обновлён');
    } catch (error) {
      console.error('Failed to update director name:', error);
    }
  };

  const handle1CSettingsSave = async () => {
    try {
      await updateSettings.mutateAsync({
        onec_api_url: onecApiUrl || undefined,
        onec_api_username: onecApiUsername || undefined,
        onec_api_password: onecApiPassword || undefined,
        onec_timeout_ms: onecTimeoutMs,
      });
      setIs1CSettingsDirty(false);
      setOnecApiPassword(''); // Clear password after save
      console.log('Настройки 1С обновлены');
    } catch (error) {
      console.error('Failed to update 1C settings:', error);
    }
  };

  const handleCopyTenantId = async () => {
    if (organizationInfo?.id) {
      try {
        await navigator.clipboard.writeText(organizationInfo.id);
        setIsTenantIdCopied(true);
        setTimeout(() => setIsTenantIdCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy tenant ID:', error);
      }
    }
  };

  const handleSync1CClick = async () => {
    try {
      setSyncResults(null);
      setShowSyncResults(true);
      const result = await sync1C.mutateAsync();
      setSyncResults(result);
    } catch (error) {
      console.error('Sync failed:', error);
      // Results dialog will show error state
    }
  };

  const handleKpiSyncClick = async () => {
    setKpiSyncResult(null);
    setKpiSyncError(null);
    try {
      const result = await kpiSync.mutateAsync({});
      setKpiSyncResult(result);
    } catch (error) {
      console.error('KPI sync failed:', error);
      setKpiSyncError(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <div className="space-y-6 flex-1 overflow-auto">
      {/* Breadcrumbs */}
      <Breadcrumbs />

      {/* Page header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <SettingsIcon className="h-8 w-8" />
          Настройки
        </h1>
        <p className="text-muted-foreground mt-2">
          Управление настройками организации и системы
        </p>
      </div>

      {/* Organization info card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Информация об организации
          </CardTitle>
          <CardDescription>
            Основные данные вашей организации
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Organization Basic Info (Read-only) */}
          <div className="grid grid-cols-2 gap-4">
            {/* Tenant ID */}
            <div className="col-span-2">
              <p className="text-sm font-medium text-muted-foreground">Tenant ID (Идентификатор организации)</p>
              <div className="flex items-center gap-2 mt-1">
                <code className="text-xs bg-muted px-2 py-1 rounded font-mono flex-1">
                  {isLoadingOrganization ? '...' : organizationInfo?.id || '—'}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyTenantId}
                  disabled={!organizationInfo?.id}
                  className="h-7 px-2"
                >
                  {isTenantIdCopied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-muted-foreground">Наименование организации</p>
              <p className="text-sm">{isLoadingOrganization ? '...' : organizationInfo?.name || '—'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">БИН/ИНН</p>
              <p className="text-sm">{isLoadingOrganization ? '...' : organizationInfo?.inn || '—'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Администратор</p>
              <p className="text-sm">{user ? getUserDisplayName(user) : '—'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Email</p>
              <p className="text-sm">{user?.email || '—'}</p>
            </div>
          </div>

          {/* Director Name (Editable) */}
          <div className="pt-3 border-t">
            <Label htmlFor="director-name" className="text-sm font-medium">
              Руководитель организации
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              ФИО генерального директора, CEO или руководителя
            </p>
            <div className="flex gap-2">
              <Input
                id="director-name"
                value={directorName}
                onChange={(e) => {
                  setDirectorName(e.target.value);
                  setIsDirectorNameDirty(true);
                }}
                placeholder="Иванов Иван Иванович"
                disabled={isLoadingSettings || updateSettings.isPending}
                className="flex-1"
              />
              <Button
                onClick={handleDirectorNameSave}
                disabled={!isDirectorNameDirty || updateSettings.isPending}
                size="default"
              >
                <Save className="h-4 w-4 mr-2" />
                Сохранить
              </Button>
            </div>
          </div>

        </CardContent>
      </Card>

      {/* QR Secret Management */}
      <QRSecretCard />

      {/* 1C Integration Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            Интеграция с 1С
          </CardTitle>
          <CardDescription>
            Настройки подключения к вашей системе 1С для синхронизации данных
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* 1C API URL */}
          <div>
            <Label htmlFor="onec-api-url" className="text-sm font-medium">
              URL 1С API
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              Адрес вашего сервера 1С (например: http://1c-server.company.local:8080/api)
            </p>
            <Input
              id="onec-api-url"
              type="url"
              value={onecApiUrl}
              onChange={(e) => {
                setOnecApiUrl(e.target.value);
                setIs1CSettingsDirty(true);
              }}
              placeholder="http://1c-server.company.local:8080/api"
              disabled={isLoadingSettings || updateSettings.isPending}
            />
          </div>

          {/* 1C API Username */}
          <div>
            <Label htmlFor="onec-api-username" className="text-sm font-medium">
              Имя пользователя
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              Логин для доступа к 1С API
            </p>
            <Input
              id="onec-api-username"
              type="text"
              value={onecApiUsername}
              onChange={(e) => {
                setOnecApiUsername(e.target.value);
                setIs1CSettingsDirty(true);
              }}
              placeholder="tco_integration"
              disabled={isLoadingSettings || updateSettings.isPending}
            />
          </div>

          {/* 1C API Password */}
          <div>
            <Label htmlFor="onec-api-password" className="text-sm font-medium">
              Пароль
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              Пароль будет зашифрован при сохранении. Оставьте пустым, чтобы не изменять.
            </p>
            <Input
              id="onec-api-password"
              type="password"
              value={onecApiPassword}
              onChange={(e) => {
                setOnecApiPassword(e.target.value);
                setIs1CSettingsDirty(true);
              }}
              placeholder="••••••••"
              disabled={isLoadingSettings || updateSettings.isPending}
            />
          </div>

          {/* 1C API Timeout */}
          <div>
            <Label htmlFor="onec-timeout" className="text-sm font-medium">
              Таймаут (мс)
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              Максимальное время ожидания ответа от 1С в миллисекундах (5000-300000)
            </p>
            <Input
              id="onec-timeout"
              type="number"
              min={5000}
              max={300000}
              step={1000}
              value={onecTimeoutMs}
              onChange={(e) => {
                setOnecTimeoutMs(parseInt(e.target.value) || 60000);
                setIs1CSettingsDirty(true);
              }}
              disabled={isLoadingSettings || updateSettings.isPending}
            />
          </div>

          {/* Save Button */}
          <div className="pt-2">
            <Button
              onClick={handle1CSettingsSave}
              disabled={!is1CSettingsDirty || updateSettings.isPending}
              size="default"
              className="w-full sm:w-auto"
            >
              <Save className="h-4 w-4 mr-2" />
              Сохранить настройки 1С
            </Button>
          </div>

          {/* Sync Button */}
          <div className="pt-2 border-t">
            <Button
              onClick={handleSync1CClick}
              disabled={!onecApiUrl || sync1C.isPending}
              size="default"
              variant="outline"
              className="w-full sm:w-auto"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${sync1C.isPending ? 'animate-spin' : ''}`} />
              Синхронизировать справочники
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              Запустить синхронизацию подразделений, должностей, графиков и сотрудников из 1С
            </p>
          </div>
        </CardContent>
      </Card>

      {/* KPI Bonus Sync */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            KPI-бонусы (kpi.sandyq.kz)
          </CardTitle>
          <CardDescription>
            Подтянуть начисления KPI-бонусов сотрудников за предыдущий и текущий месяц в раздел зарплаты.
            Учитываются только организации с включённой интеграцией.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            onClick={handleKpiSyncClick}
            disabled={kpiSync.isPending}
            size="default"
            variant="outline"
            className="w-full sm:w-auto"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${kpiSync.isPending ? 'animate-spin' : ''}`} />
            {kpiSync.isPending ? 'Синхронизация…' : 'Синхронизировать KPI-бонусы'}
          </Button>

          {kpiSyncResult && (
            <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1">
              <div className="font-medium">
                Готово: {kpiSyncResult.status === 'success' ? 'успешно' : kpiSyncResult.status === 'partial' ? 'частично' : 'ошибка'}
              </div>
              <div className="text-muted-foreground">
                Период: {kpiSyncResult.month_from} → {kpiSyncResult.month_to}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2">
                <div>
                  <div className="text-xs text-muted-foreground">Обработано</div>
                  <div className="font-medium">{kpiSyncResult.employees_processed}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">С бонусами</div>
                  <div className="font-medium">{kpiSyncResult.employees_with_bonuses}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Записей в salary_data</div>
                  <div className="font-medium">{kpiSyncResult.salary_rows_upserted}</div>
                </div>
              </div>
              {kpiSyncResult.errors.length > 0 && (
                <div className="pt-2 text-xs text-red-600">
                  Ошибок: {kpiSyncResult.errors.length}. Первая: {kpiSyncResult.errors[0].employee_number ?? kpiSyncResult.errors[0].employee_id} — {kpiSyncResult.errors[0].error}
                </div>
              )}
            </div>
          )}

          {kpiSyncError && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{kpiSyncError}</span>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Список организаций, по которым тянутся бонусы, настраивается флагом <code>kpi_sync_enabled</code> на организации.
            Ежедневная синхронизация запускается автоматически в 03:00.
          </p>
        </CardContent>
      </Card>

      {/* Sync Results Dialog */}
      <SyncResultsDialog
        open={showSyncResults}
        onOpenChange={setShowSyncResults}
        syncResult={syncResults}
        isLoading={sync1C.isPending}
      />
    </div>
  );
}
